import React, { useCallback } from "react";
import { ErrorBoundary, LottieAnimation } from "@swiggy-private/core-ui";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { getNRErrorInfo } from "@swiggy-private/js-utils";
import { LottieOverlay } from "./styles";

const LOTTIE_ERROR_TYPE = {
    REACT_ERROR_BOUNDARY: "REACT_ERROR_BOUNDARY",
    LOTTIE_LOAD_ERROR: "LOTTIE_LOAD_ERROR",
} as const;

type LottieErrorType = (typeof LOTTIE_ERROR_TYPE)[keyof typeof LOTTIE_ERROR_TYPE];

export interface DeliveryModeLottieProps {
    animationUrl: string;
    shouldPlayAnimation: boolean;
    onAnimationComplete: VoidFunction;
}

const DeliveryModeLottie: React.FC<DeliveryModeLottieProps> = ({
    animationUrl,
    shouldPlayAnimation,
    onAnimationComplete,
}) => {
    const onLottieError = useCallback(
        (lottieErrorType: LottieErrorType, error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);
            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                widgetType: "DELIVERY_MODE_LOTTIE",
                lottieErrorType,
                ...errorEvents,
            });
        },
        [],
    );

    return (
        <LottieOverlay $blocking={shouldPlayAnimation}>
            <ErrorBoundary
                errorContent={null}
                errorFallback={(error, errorInfo) =>
                    onLottieError(LOTTIE_ERROR_TYPE.REACT_ERROR_BOUNDARY, error, errorInfo)
                }
            >
                <LottieAnimation
                    fileName={animationUrl}
                    visible={shouldPlayAnimation}
                    playLottie={shouldPlayAnimation}
                    autoplay={shouldPlayAnimation}
                    loop={false}
                    playIfVisible={false}
                    backwardSpeed={1}
                    forwardSpeed={1}
                    onPlayComplete={onAnimationComplete}
                    onError={() => onLottieError(LOTTIE_ERROR_TYPE.LOTTIE_LOAD_ERROR)}
                />
            </ErrorBoundary>
        </LottieOverlay>
    );
};

export default DeliveryModeLottie;
